package co.uk.paxes.pinpoint.model

/**
 * Expert difficulty AI strategy
 * - Uses aggressive offensive strategy (90% offense, 10% defense)
 * - Comprehensive joker evaluation with strategic bonuses
 * - Looks for maximum scoring opportunities
 * - No randomness - always optimal play
 */
class ExpertStrategy(val playerId: String) : AIStrategy {
    companion object {
        const val VERSION = "2.2.0"
    }
    
    // Tracks positions where the AI places jokers during the game
    private val placedJokerPositions: MutableList<Position> = mutableListOf()

    // Public accessor for testing purposes
    fun getPlacedJokerPositions(): List<Position> = placedJokerPositions
    
    private val scoreCalculator = ScoreCalculator()
    
    override fun selectMove(
        gameState: GameState,
        availablePins: List<Pin>,
        boardAnalyzer: BoardAnalyzer
    ): Move? {
        val emptyPositions = boardAnalyzer.getEmptyPositions(gameState.board)
        if (emptyPositions.isEmpty() || availablePins.isEmpty()) {
            return null
        }
        
        // DEBUG: Log current board state
        println("ExpertStrategy: 🔍 DEBUG - Board has ${gameState.board.getOccupiedPositions().size} pins")
        val occupiedPositions = gameState.board.getOccupiedPositions()
        val pinsByColor = occupiedPositions.groupBy { pos ->
            gameState.board.getPinAt(pos)?.color
        }
        pinsByColor.forEach { (color, positions) ->
            println("ExpertStrategy: 🔍 DEBUG - $color: ${positions.size} pins at ${positions.take(5)}")
        }
        
        // Check for critical blocking opportunities (5+ points)
        val blockingOpps = boardAnalyzer.findBlockingOpportunities(gameState)
        val criticalBlock = blockingOpps.firstOrNull { it.potentialScore >= 5 }
        
        if (criticalBlock != null) {
            val blockingPin = availablePins.firstOrNull()
            if (blockingPin != null) {
                println("ExpertStrategy: 🎯 BLOCKING critical ${criticalBlock.potentialScore}-point move")
                return Move(playerId, blockingPin, criticalBlock.position)
            }
        }
        
        // Use comprehensive evaluation to find optimal move
        var bestMove: Move? = null
        var bestValue = Int.MIN_VALUE
        
        println("ExpertStrategy: 🧠 EXPERT AI: Evaluating ${availablePins.size} pins × ${emptyPositions.size} positions")
        
        val currentPlayer = gameState.currentPlayer
        
        try {
            // EXPERT STRATEGY: Prioritize OFFENSE over defense
            // Evaluate ALL moves (normal + joker) and pick the absolute best
            
            val allMoves = mutableListOf<Pair<Move, Int>>()
            
            // PHASE 1: Evaluate normal moves
            println("ExpertStrategy: 📊 Phase 1: Evaluating normal moves")
            println("ExpertStrategy: 📊 Available pins: ${availablePins.map { it.color }}")
            println("ExpertStrategy: 📊 Empty positions: ${emptyPositions.size}")
            
            for (pin in availablePins) {
                for (position in emptyPositions) {
                    val move = Move(playerId, pin, position)
                    val scoreResult = scoreCalculator.calculateMoveScore(gameState.board, move)
                    val score = scoreResult.points
                    
                    // DEBUG: Log ALL moves with score > 0 to see what's being evaluated
                    if (score > 0) {
                        val (row, col) = position.toLegacy()
                        println("ExpertStrategy: 📍 ${pin.color} at ($row,$col) = $score points")
                    }
                    
                    // Expert AI: ULTRA-AGGRESSIVE offense (90% offense, 10% defense)
                    var totalValue = score * 5 // Massive weight on scoring
                    
                    // Align structural bonuses with joker logic to avoid preferring low-score jokers over high-score normal moves
                    if (score >= 10) {
                        totalValue += 500 // Huge bonus for 6-line blackout moves
                    } else if (score >= 5) {
                        totalValue += 200 // Big bonus for 5+ point moves
                    } else if (score >= 3) {
                        totalValue += 100 // Good bonus for 3+ point moves
                    }
                    
                    // Block critical threats (6+ points) but with lower priority
                    for (color in PinColor.values()) {
                        if (color == PinColor.BLACK) continue
                        try {
                            val opponentScore = boardAnalyzer.simulateScore(gameState.board, position, color)
                            if (opponentScore >= 6) {
                                totalValue += opponentScore / 2 // Minimal blocking weight
                            }
                        } catch (e: Exception) {
                            continue
                        }
                    }
                    
                    allMoves.add(Pair(move, totalValue))
                    
                    if (score >= 3) {
                        println("ExpertStrategy:   💎 HIGH SCORE: ${pin.color} at $position = $score points (weighted: $totalValue)")
                    }
                }
            }
            
            // PHASE 2: ENHANCED JOKER EVALUATION - Always consider jokers when available
            if (currentPlayer.canUseJoker() && availablePins.isNotEmpty()) {
                println("ExpertStrategy: 🃏 Evaluating jokers (${currentPlayer.jokersUsed}/4 used)")
                
                // Find the best normal move ACTUAL score AND weighted value for comparison
                var bestNormalActualScore = 0
                var bestNormalWeightedValue = 0
                for (move in allMoves) {
                    if (!move.first.pin.isJoker) {
                        val scoreResult = scoreCalculator.calculateMoveScore(gameState.board, move.first)
                        if (scoreResult.points > bestNormalActualScore) {
                            bestNormalActualScore = scoreResult.points
                        }
                        if (move.second > bestNormalWeightedValue) {
                            bestNormalWeightedValue = move.second
                        }
                    }
                }
                
                // Expert AI: Evaluate jokers more intelligently
                for (sacrificePin in availablePins) {
                    for (jokerColor in PinColor.values()) {
                        if (jokerColor == PinColor.BLACK) continue
                        if (jokerColor == sacrificePin.color) continue
                        
                        val jokerPin = Pin.joker(sacrificePin.color, jokerColor)
                        
                        // Find the BEST position for this joker color
                        var bestJokerScore = 0
                        var bestJokerPosition: Position? = null
                        
                        for (position in emptyPositions) {
                            val jokerMove = Move(playerId, jokerPin, position)
                            val jokerScoreResult = scoreCalculator.calculateMoveScore(gameState.board, jokerMove)
                            val jokerScore = jokerScoreResult.points
                            
                            if (jokerScore > bestJokerScore) {
                                bestJokerScore = jokerScore
                                bestJokerPosition = position
                            }
                        }
                        
                        // Only add joker move if it's the best position for this color
                        if (bestJokerPosition != null && bestJokerScore > 0) {
                            val jokerMove = Move(playerId, jokerPin, bestJokerPosition)
                            
                            // ULTRA-AGGRESSIVE JOKER LOGIC: Always prefer jokers when they score well
                            val jokersRemaining = 4 - currentPlayer.jokersUsed
                            
                            // Base value: Apply same scoring weight as normal moves
                            var jokerTotalValue = bestJokerScore * 5
                            
                            // ALWAYS add strategic bonuses for jokers (don't waste them!)
                            if (bestJokerScore > 0) {
                                // Massive bonus for high-scoring jokers
                                if (bestJokerScore >= 10) {
                                    jokerTotalValue += 500 // Huge bonus for 6-line blackout moves
                                } else if (bestJokerScore >= 5) {
                                    jokerTotalValue += 200 // Big bonus for 5+ point moves
                                } else if (bestJokerScore >= 3) {
                                    jokerTotalValue += 100 // Good bonus for 3+ point moves
                                }
                                
                                // Additional bonus if joker beats best normal move
                                if (bestJokerScore > bestNormalActualScore) {
                                    val scoreDifference = bestJokerScore - bestNormalActualScore
                                    jokerTotalValue += scoreDifference * 30 // Extra bonus for beating normal moves
                                }
                                
                                // Use jokers before they're wasted
                                if (jokersRemaining >= 3) {
                                    jokerTotalValue += 50 // Bonus for using jokers early
                                } else if (jokersRemaining <= 2) {
                                    jokerTotalValue += 150 // Urgency bonus - don't waste remaining jokers
                                }
                                
                                // Board nearly full - use jokers now or lose them
                                if (emptyPositions.size <= 15) {
                                    jokerTotalValue += 100
                                }
                            }
                            
                            allMoves.add(Pair(jokerMove, jokerTotalValue))
                            
                            println("ExpertStrategy:   🃏 ${sacrificePin.color}→$jokerColor at $bestJokerPosition = $bestJokerScore pts (value: $jokerTotalValue)")
                        }
                    }
                }
            }
            
            // Pick the BEST move overall (offense-focused)
            if (allMoves.isNotEmpty()) {
                val sortedMoves = allMoves.sortedByDescending { it.second }
                bestMove = sortedMoves.first().first
                bestValue = sortedMoves.first().second
                
                val topMoves = sortedMoves.take(5)
                println("ExpertStrategy: 🏆 Top 5 moves:")
                topMoves.forEachIndexed { index, (move, value) ->
                    val moveType = if (move.pin.isJoker) "🃏JOKER" else "NORMAL"
                    val actualScore = scoreCalculator.calculateMoveScore(gameState.board, move).points
                    println("ExpertStrategy:   ${index + 1}. $moveType ${move.pin.color} at ${move.position} = $actualScore pts (weighted: $value)")
                }
            }
            
        } catch (e: Exception) {
            println("ExpertStrategy: Error in evaluation: ${e.message}")
            if (bestMove == null) {
                val randomPin = availablePins.random()
                val randomPosition = emptyPositions.random()
                return Move(playerId, randomPin, randomPosition)
            }
        }
        
        println("ExpertStrategy: ✅ Selected move")
        if (bestMove?.pin?.isJoker == true) {
            placedJokerPositions.add(bestMove.position)
        }
        return bestMove
    }
    
    override fun shouldCreateJoker(
        gameState: GameState,
        availablePins: List<Pin>,
        boardAnalyzer: BoardAnalyzer
    ): Boolean {
        val currentPlayer = gameState.currentPlayer
        if (!currentPlayer.canUseJoker()) {
            return false
        }
        
        // Exhaustively evaluate all joker possibilities
        val emptyPositions = boardAnalyzer.getEmptyPositions(gameState.board)
        val bestNormalScore = boardAnalyzer.findScoringOpportunities(gameState, availablePins)
            .maxOfOrNull { it.potentialScore } ?: 0
        
        var bestJokerScore = 0
        
        for (color in PinColor.values()) {
            if (color == PinColor.BLACK) continue
            
            for (position in emptyPositions) {
                val score = boardAnalyzer.simulateScore(gameState.board, position, color)
                if (score > bestJokerScore) {
                    bestJokerScore = score
                }
            }
        }
        
        val jokersRemaining = 4 - currentPlayer.jokersUsed
        
        // IMPROVED LOGIC: More aggressive joker usage
        // 1. Always use joker if it scores more than normal moves
        if (bestJokerScore > bestNormalScore) {
            println("ExpertStrategy: 🃏 JOKER RECOMMENDED: Scores $bestJokerScore vs normal $bestNormalScore")
            return true
        }
        
        // 2. Use jokers in end-game even if equal score (don't waste them)
        if (bestJokerScore == bestNormalScore && bestJokerScore > 0 && jokersRemaining <= 2) {
            println("ExpertStrategy: 🃏 END-GAME JOKER: Equal score but few jokers left")
            return true
        }
        
        // 3. Use jokers when board is nearly full (last chance)
        if (emptyPositions.size <= 5 && bestJokerScore > 0 && jokersRemaining > 0) {
            println("ExpertStrategy: 🃏 LAST-CHANCE JOKER: Board nearly full")
            return true
        }
        
        // 4. Use joker if it provides any advantage (even 1 point difference)
        return bestJokerScore > bestNormalScore
    }
    
    /**
     * Re‑evaluate the hand after a joker has been placed.
     * Updates availablePins (removes the used pin, adds any new pins if applicable).
     * Re-runs joker evaluation with updated jokersRemaining.
     * Returns the next optimal move if another joker can improve the score.
     */
    fun reassessHandAfterJoker(gameState: GameState, availablePins: List<Pin>, boardAnalyzer: BoardAnalyzer): Move? {
        val currentPlayer = gameState.currentPlayer
        val jokersRemaining = 4 - currentPlayer.jokersUsed
        if (jokersRemaining <= 0) {
            return null
        }
        
        // Ensure we pass the updated hand.
        return selectMove(gameState, availablePins, boardAnalyzer)
    }
    
    override fun selectJokerColor(
        gameState: GameState,
        boardAnalyzer: BoardAnalyzer
    ): PinColor? {
        // Evaluate each color exhaustively
        val emptyPositions = boardAnalyzer.getEmptyPositions(gameState.board)
        var bestColor: PinColor? = null
        var bestScore = 0
        
        for (color in PinColor.values()) {
            if (color == PinColor.BLACK) continue
            
            var colorBestScore = 0
            for (position in emptyPositions) {
                val score = boardAnalyzer.simulateScore(gameState.board, position, color)
                if (score > colorBestScore) {
                    colorBestScore = score
                }
            }
            
            if (colorBestScore > bestScore || (colorBestScore == bestScore && color == PinColor.GREEN && bestScore > 0)) {
                bestScore = colorBestScore
                bestColor = color
            }
        }
        
        // Provide tie-breaking logic.
        if (bestScore == 0) {
            return PinColor.GREEN
        }
        
        return bestColor
    }
}
